import {
	infiniteQueryOptions,
	queryOptions,
	useQuery,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchAutomationSettingsList,
	fetchEventSources,
	fetchGraylogAutomationSettingsDetails,
	fetchGraylogEvents,
	fetchGraylogTriggeredAlerts,
} from './api'

import type { TriggerAlertFilters } from './types'

/**
 * Query keys for Graylog
 */
export const graylogKeys = {
	all: ['graylog'] as const,
	events: (tenant: string) => [...graylogKeys.all, 'events', tenant] as const,
	sources: (tenant: string) =>
		[...graylogKeys.events(tenant), 'sources'] as const,
	triggeredAlerts: (tenant: string, filters?: TriggerAlertFilters) =>
		[...graylogKeys.all, 'triggeredAlerts', tenant, filters] as const,
	automations: () => [...graylogKeys.all, 'automations'] as const,
	details: () => [...graylogKeys.automations(), 'details'] as const,
	detail: (tenant: string, rule_id: string | null) =>
		[...graylogKeys.details(), tenant, rule_id] as const,
	settings: (tenant: string, limit = 20) =>
		[...graylogKeys.automations(), tenant, 'settings', 'list', limit] as const,
}

/**
 * Query options for Graylog
 */
export const graylogQueries = {
	events: (tenant: string) =>
		queryOptions({
			queryKey: graylogKeys.events(tenant),
			queryFn: () => fetchGraylogEvents(tenant),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 30, 'm'),
		}),

	triggeredAlerts: (tenant: string, filters?: TriggerAlertFilters) =>
		queryOptions({
			queryKey: graylogKeys.triggeredAlerts(tenant, filters),
			queryFn: () => fetchGraylogTriggeredAlerts(tenant, filters),
			refetchInterval: time(5, 'm'),
			gcTime: time(10, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
		}),

	automationDetail: (tenant: string, rule_id: string | null) =>
		queryOptions({
			queryKey: graylogKeys.detail(tenant, rule_id),
			queryFn: ({ signal }) =>
				fetchGraylogAutomationSettingsDetails(tenant, rule_id, signal),
			gcTime: time(30, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 15, 'm'),
			enabled: !!tenant && !!rule_id,
		}),

	automationSettings: (tenant: string, limit = 20) =>
		infiniteQueryOptions({
			queryKey: graylogKeys.settings(tenant, limit),
			queryFn: ({ pageParam = 1 }) =>
				fetchAutomationSettingsList(tenant, pageParam, limit),
			gcTime: time(3, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				if (lastPage.rules.length < limit) {
					return undefined
				}

				return lastPageParam + 1
			},
			initialPageParam: 1,
		}),

	eventSources: (tenant: string) =>
		queryOptions({
			queryKey: graylogKeys.sources(tenant),
			queryFn: ({ signal }) => fetchEventSources(tenant, signal),
			gcTime: time(10, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
		}),
}

/**
 * Custom hook to fetch Graylog events using ReactQuery
 */
export const useGraylogEvents = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(graylogQueries.events(tenant))
}

/**
 * Custom hook to fetch Triggered Alerts from Graylog
 * @param filters - Optional filters for time range and pagination
 */
export const useGraylogTriggeredAlerts = (filters?: TriggerAlertFilters) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(graylogQueries.triggeredAlerts(tenant, filters))
}

/**
 * Custom hook to fetch Graylog Automation Settings Details synchronously
 * @param rule_id - The rule ID
 */
export const useAutomationDetailsSync = (rule_id: string | null) => {
	const tenant = useCurrentTenant()

	return useQuery({
		...graylogQueries.automationDetail(tenant, rule_id),
		enabled: !!tenant && !!rule_id,
	})
}

/**
 * Custom hook to fetch Graylog Automation Settings
 */
export const useAutomationSettings = (limit = 20) => {
	const tenant = useCurrentTenant()

	return useSuspenseInfiniteQuery(
		graylogQueries.automationSettings(tenant, limit),
	)
}

/**
 * Custom hook to fetch Graylog Event Sources
 */
export const useEventSources = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(graylogQueries.eventSources(tenant))
}
